import {
  RecursivePartialSiteAllowStringValues,
  SiteAllowStringValues,
} from "@inrange/building-manager-api-client/models-site";
import {
  SiteCalculationsContext,
  useContextTS,
} from "@inrange/shared-components";
import { useState } from "react";
import { Alert, Button, Form, Modal, Row } from "react-bootstrap";
import { OPERATIONAL_STATUSES } from "../operationalStatusUtils";

const SaveWithNoteModal = ({
  saveWithNote,
  cancel,
}: {
  saveWithNote: (note: string) => void;
  cancel: () => void;
}) => {
  const [saveNote, setSaveNote] = useState("");
  return (
    <Modal show={true} onHide={cancel} centered size="lg">
      <Modal.Body id={"saveSiteWithNote"}>
        <div className="mb-3">Add a short note describing the change made:</div>
        <div>
          <Form.Group className="mb-3" controlId="saveNote">
            <Form.Control
              as="textarea"
              rows={2}
              value={saveNote}
              onChange={(e) => setSaveNote(e.target.value)}
              autoFocus
              data-testid="site-save-notes"
            />
          </Form.Group>
        </div>
        <div
          style={{
            display: "flex",
            gap: 24,
            justifyContent: "center",
            marginTop: 8,
          }}
        >
          <Button variant="secondary" size="sm" onClick={cancel}>
            Cancel
          </Button>
          <Button
            variant="success"
            size="sm"
            onClick={() => saveWithNote(saveNote)}
            data-testid="save-site-with-note"
          >
            Save site
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

const ActionButtons = ({
  site,
  handleDeleteClick,
  handlePreviewChangelog,
  handleSaveClick,
  saveDisabled,
  allowNote,
}: {
  site: SiteAllowStringValues;
  handleDeleteClick?: () => void;
  handlePreviewChangelog?: () => void;
  handleSaveClick: (withExtraUpdates?: {
    site?: RecursivePartialSiteAllowStringValues;
    [key: string]: any;
  }) => void;
  saveDisabled: boolean;
  allowNote: boolean;
}) => {
  const [showSaveWithNoteModal, setShowSaveWithNoteModal] = useState(false);
  const { errors, clientsideValidations, siteCalculations } = useContextTS(
    SiteCalculationsContext
  );
  const errorsWithoutGenerationDemandChanges = { ...errors };
  const blockedGenerationDemandChangesKey = Object.keys(
    errorsWithoutGenerationDemandChanges
  ).find((key) => key.endsWith(".sdmInputsDataHash"));
  const blockedGenerationDemandChanges = blockedGenerationDemandChangesKey
    ? errorsWithoutGenerationDemandChanges[blockedGenerationDemandChangesKey]
    : undefined;
  if (blockedGenerationDemandChangesKey) {
    delete errorsWithoutGenerationDemandChanges[
      blockedGenerationDemandChangesKey
    ];
  }

  const isOutOfBounds =
    clientsideValidations.isTenantTariffOutOfBounds ||
    clientsideValidations.isNetworkImportTariffOutOfBounds ||
    clientsideValidations.isIRROutOfBounds;

  const activeInvestmentModel = siteCalculations.activeInvestmentModel;
  const licenseArrLandlord =
    siteCalculations.financialModels?.landlord?.license?.inrangeNetRevenue;
  const licenseArrOO =
    siteCalculations.financialModels?.ownerOccupier?.license?.inrangeNetRevenue;
  const isLicenseArrNotPositive =
    (licenseArrLandlord !== undefined && licenseArrLandlord <= 0) ||
    (licenseArrOO !== undefined && licenseArrOO <= 0);
  const leaseArr = siteCalculations.financialModelsInrange?.lease?.netRevenue;
  const isLeaseArrNotPositive = leaseArr !== undefined && leaseArr <= 0;

  let largeButtonCount = 1; // Save button is always present
  if (allowNote) {
    largeButtonCount += 1;
  }

  return (
    <>
      <Row
        style={{
          backgroundColor: "white",
          margin: "8px 0px 0px",
          padding: "0 12px",
        }}
      >
        <div
          className="action-buttons-grid"
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(2, 1fr) 1fr repeat(${largeButtonCount}, 1.5fr)`,
            gap: "10px 32px",
            padding: "20px 0px 20px",
            justifyContent: "center",
            alignItems: "stretch",
          }}
        >
          {handleDeleteClick && (
            <DeleteButton site={site} handleDeleteClick={handleDeleteClick} />
          )}
          {!handleDeleteClick && <div></div>}
          {handlePreviewChangelog && (
            <Button
              variant="inrangesecondary"
              onClick={handlePreviewChangelog}
              disabled={!site?.id}
            >
              Preview changelog
            </Button>
          )}
          {!handlePreviewChangelog && <div></div>}
          <div className="action-buttons-spacer"></div>
          {allowNote && (
            <Button
              variant="success"
              onClick={() => setShowSaveWithNoteModal(true)}
              disabled={
                saveDisabled ||
                Object.keys(errorsWithoutGenerationDemandChanges).length > 0
              }
            >
              Save site with note
            </Button>
          )}
          <Button
            variant="success"
            onClick={() => handleSaveClick(undefined)}
            disabled={
              saveDisabled ||
              Object.keys(errorsWithoutGenerationDemandChanges).length > 0
            }
          >
            Save site
          </Button>
          <div
            className="text-danger card-text action-buttons-errors"
            style={{
              fontSize: "14px",
              gridColumn: "4 / 4",
              gridRow: "2 / 2",
            }}
          >
            {Object.keys(errorsWithoutGenerationDemandChanges).length > 0 && (
              <div>
                The form has validation errors. Please address the errors marked
                on the page.
              </div>
            )}
            {blockedGenerationDemandChanges && (
              <div>{blockedGenerationDemandChanges}</div>
            )}
            {isOutOfBounds && (
              <div>At least one value is outside of defined bounds</div>
            )}
            {activeInvestmentModel === "license" && isLicenseArrNotPositive && (
              <div>
                IR net ARR (Y1, License) is not positive - site will not make
                money
              </div>
            )}
            {activeInvestmentModel === "lease" && isLeaseArrNotPositive && (
              <div>
                IR net ARR (Y2, Lease) is not positive - site will not make
                money
              </div>
            )}
          </div>
        </div>
        {handleDeleteClick &&
          OPERATIONAL_STATUSES.has(site.operationalStatus ?? "") && (
            <Alert
              variant="secondary"
              className="mb-2"
              style={{ padding: "8px 12px" }}
            >
              Operational sites can{"'"}t be deleted
            </Alert>
          )}
      </Row>
      {showSaveWithNoteModal && (
        <SaveWithNoteModal
          saveWithNote={(note: string) => {
            setShowSaveWithNoteModal(false);
            handleSaveClick({ userNote: note });
          }}
          cancel={() => setShowSaveWithNoteModal(false)}
        />
      )}
    </>
  );
};

export default ActionButtons;

const DeleteButton = ({
  site,
  handleDeleteClick,
}: {
  site: { id?: string; operationalStatus?: string };
  handleDeleteClick: () => void;
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const handleCancelDelete = () => setShowDeleteModal(false);
  const isOperational = OPERATIONAL_STATUSES.has(site.operationalStatus ?? "");
  return (
    <>
      <Modal
        show={showDeleteModal}
        onHide={handleCancelDelete}
        style={{ position: "relative", top: "-200px" }}
      >
        <Modal.Body>
          Are you sure you want to delete this site? Deleting it will result in
          the permanent loss of all data and cannot be undone
          <div
            style={{
              display: "flex",
              gap: 24,
              justifyContent: "center",
              marginTop: 8,
            }}
          >
            <Button variant="secondary" size="sm" onClick={handleCancelDelete}>
              Cancel
            </Button>
            <Button variant="danger" size="sm" onClick={handleDeleteClick}>
              Delete site
            </Button>
          </div>
        </Modal.Body>
      </Modal>
      <Button
        variant="danger"
        onClick={() => setShowDeleteModal(true)}
        disabled={!site?.id || isOperational}
        title={isOperational ? "Cannot delete an operational site" : undefined}
      >
        Delete site
      </Button>
    </>
  );
};
